/**
 * Page state lives in the query string, so a view can be reloaded or shared:
 * `?kind=withdrawal&category=groceries&size=50&page=3&id=<uuid>`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { CategoryFilter, OperationKind } from '../api/types';

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

const KINDS: readonly string[] = ['deposit', 'withdrawal'];
/**
 * A category is checked by shape rather than against a list: the list is kept
 * on the server and added to through MCP, so a link to a category opened after
 * this page was loaded still opens the view it was shared for. A name that no
 * category goes by returns nothing, which is what the server says too.
 */
const CATEGORY_NAME = /^[a-z][a-z0-9_]*$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PageState {
  kind: OperationKind | null;
  /** One category, their absence, or null for no filter at all. */
  category: CategoryFilter | null;
  pageSize: number;
  /** Zero-based here, one-based in the URL. */
  page: number;
  selectedId: string | null;
}

/** Anything unparseable in the query string falls back to the default. */
export function readState(search: string): PageState {
  const params = new URLSearchParams(search);
  const kind = params.get('kind');
  const category = params.get('category');
  const size = Number(params.get('size'));
  const page = Number(params.get('page'));
  const id = params.get('id');

  return {
    kind: kind !== null && KINDS.includes(kind) ? (kind as OperationKind) : null,
    category:
      category !== null && CATEGORY_NAME.test(category) ? (category as CategoryFilter) : null,
    pageSize: PAGE_SIZES.includes(size as (typeof PAGE_SIZES)[number]) ? size : DEFAULT_PAGE_SIZE,
    page: Number.isInteger(page) && page > 1 ? page - 1 : 0,
    selectedId: id !== null && UUID.test(id) ? id : null,
  };
}

/** Defaults are left out, so the plain view keeps a clean URL. */
export function writeState(state: PageState): string {
  const params = new URLSearchParams();
  if (state.kind !== null) {
    params.set('kind', state.kind);
  }
  if (state.category !== null) {
    params.set('category', state.category);
  }
  if (state.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('size', String(state.pageSize));
  }
  if (state.page > 0) {
    params.set('page', String(state.page + 1));
  }
  if (state.selectedId !== null) {
    params.set('id', state.selectedId);
  }

  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/** Marks the history entry an open details drawer added, so back can close it. */
const DRAWER_ENTRY = 'ledger:operation';

export interface PageStateControls {
  state: PageState;
  /** Filters and paging replace the current entry: back should not walk them. */
  update: (patch: Partial<PageState>) => void;
  openOperation: (id: string) => void;
  closeOperation: () => void;
}

/**
 * Keeps the state and the address bar in step, in both directions: changes are
 * written back into the URL, and the browser's back button is followed.
 */
export function usePageState(): PageStateControls {
  const [state, setState] = useState<PageState>(() => readState(window.location.search));
  const pushNext = useRef(false);

  useEffect(() => {
    const onPopState = () => setState(readState(window.location.search));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const next = `${window.location.pathname}${writeState(state)}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) {
      if (pushNext.current) {
        window.history.pushState({ [DRAWER_ENTRY]: true }, '', next);
      } else {
        window.history.replaceState(window.history.state, '', next);
      }
    }
    pushNext.current = false;
  }, [state]);

  const update = useCallback((patch: Partial<PageState>) => {
    setState((previous) => ({ ...previous, ...patch }));
  }, []);

  const openOperation = useCallback((id: string) => {
    pushNext.current = true;
    setState((previous) => ({ ...previous, selectedId: id }));
  }, []);

  const closeOperation = useCallback(() => {
    // Undo our own history entry when there is one, so back and the close
    // button leave the same trail behind.
    if (isDrawerEntry(window.history.state)) {
      window.history.back();
      return;
    }
    setState((previous) => ({ ...previous, selectedId: null }));
  }, []);

  return { state, update, openOperation, closeOperation };
}

function isDrawerEntry(historyState: unknown): boolean {
  return (
    typeof historyState === 'object' &&
    historyState !== null &&
    (historyState as Record<string, unknown>)[DRAWER_ENTRY] === true
  );
}
